// src/components/auth/SignOutQuickAction.tsx
//
// Sign-out quick action for the SuiteShell header (ui-shell
// `HeaderQuickActions` slot). Renders as a small account button with a
// popover menu containing "Sign out".
//
// The @levelup/ui-shell package owns the user avatar itself (T3 initials
// circle in the header); we cannot attach a dropdown to that element from
// here. This component sits next to it in the same header row and gives
// users the same "click for menu → sign out" affordance without touching
// the shell package.
//
// Delegates the actual logout to `initiateCentralLogout` in
// `config/centralAuth.ts` — the shared, fail-closed logout implementation
// that handles both RP/BFF and legacy Bearer modes.

import { useEffect, useRef, useState } from 'react';
import type { HeaderQuickActionProps } from '@levelup/ui-shell';
import { initiateCentralLogout } from '../../config/centralAuth';

export function SignOutQuickAction(_props: HeaderQuickActionProps) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSignOut = async () => {
    if (signingOut) return;
    setError(null);
    setSigningOut(true);
    try {
      await initiateCentralLogout();
      // The logout helper navigates the window on success; execution here
      // typically stops before we return. If it does return (edge cases),
      // leave `signingOut` true so the button stays disabled during the
      // browser's own navigation handoff.
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign out failed';
      setError(message);
      setSigningOut(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-2 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title="Account menu"
        data-testid="signout-quick-action-trigger"
      >
        {/* Person icon */}
        <svg
          className="w-5 h-5 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        {/* Chevron */}
        <svg
          className="w-3 h-3 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 mt-2 w-44 rounded-lg border border-gray-200 bg-white shadow-lg py-1 z-50"
          data-testid="signout-quick-action-menu"
        >
          <button
            role="menuitem"
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full text-left px-3 py-2 min-h-[44px] text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="signout-quick-action-signout"
          >
            {/* Exit icon */}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
          {error && (
            <p
              role="alert"
              className="px-3 py-1 text-xs text-red-600 border-t border-gray-100"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default SignOutQuickAction;
